// FormYearPicker.tsx
import React from 'react';
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldLabel from './FieldLabel';
import FieldError from './FieldError';
import FieldDescribe from './FieldDescribe';
import YearPicker from '../../datePicker/YearPicker';

type Props<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  label: string;
  placeholder?: string;
  isRequired?: boolean;
  disabled?: boolean;
  describe?: React.ReactNode;
  minYear?: number;
  maxYear?: number;
  id?: string;
};

const FormYearPicker = <TFieldValues extends FieldValues>({
  name,
  label,
  placeholder,
  isRequired,
  disabled,
  describe,
  minYear,
  maxYear,
  id,
}: Props<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: isRequired ? { required: 'This field is required' } : undefined,
  });

  return (
    <div className="flex flex-col gap-2">
      <FieldLabel name={name} label={label} isRequired={isRequired} />
      <YearPicker
        id={id ?? String(name)}
        name={String(name)}
        value={field.value ?? ''}
        onChange={(val) => field.onChange(val)}
        isDisabled={disabled}
        hasError={!!error}
        placeholder={placeholder}
        minYear={minYear}
        maxYear={maxYear}
        onBlur={field.onBlur}
      />
      {error ? (
        <FieldError>{error.message}</FieldError>
      ) : (
        describe && <FieldDescribe>{describe}</FieldDescribe>
      )}
    </div>
  );
};

export default FormYearPicker;
